import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'

import type {AgentInfo} from '@github-ui/agent-sessions-types/agent-identity'
import {ExpandableMarkdown} from '../../expandable-markdown/ExpandableMarkdown'
import styles from './UserMessage.module.css'
import {AGENT_MENTION_REGEX, matchAgentMention} from '../../../utils/markdown-link-utils'

export const MessageTextWithHovercards = ({body, agentInfo}: {body: string; agentInfo: AgentInfo}) => {
  const parts = parseBodyWithMentions(body, agentInfo)

  if (parts.length === 0) {
    return <ExpandableMarkdown className={styles.messageText} markdown={body} />
  }

  return (
    <div className={styles.messageText}>
      {parts.map(({key, element}) => (
        <span key={key}>{element}</span>
      ))}
    </div>
  )
}

function parseBodyWithMentions(body: string, agentInfo: AgentInfo) {
  const parts: Array<{key: string; element: React.ReactElement}> = []
  // Create a new regex instance to avoid stateful issues with global flag
  const mentionRegex = new RegExp(AGENT_MENTION_REGEX.source, AGENT_MENTION_REGEX.flags)
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = mentionRegex.exec(body)) !== null) {
    // Add any text before this mention
    addTextPart(parts, body, lastIndex, match.index)

    // Add the mention (as link or text)
    const mentionText = match[0]
    addMentionPart(parts, mentionText, match.index, agentInfo)

    lastIndex = match.index + mentionText.length
  }

  // Add any remaining text after the last mention
  addTextPart(parts, body, lastIndex, body.length)

  return parts
}

function addTextPart(
  parts: Array<{key: string; element: React.ReactElement}>,
  body: string,
  startIndex: number,
  endIndex: number,
) {
  if (endIndex > startIndex) {
    const textContent = body.slice(startIndex, endIndex)
    parts.push({
      key: `text-${startIndex}`,
      element: <ExpandableMarkdown markdown={textContent} />,
    })
  }
}

function addMentionPart(
  parts: Array<{key: string; element: React.ReactElement}>,
  mentionText: string,
  index: number,
  agentInfo: AgentInfo,
) {
  const mentionElement = <MentionElement mentionText={mentionText} agentInfo={agentInfo} />
  parts.push({
    key: `link-${index}`,
    element: mentionElement,
  })
}

function MentionElement({mentionText, agentInfo}: {mentionText: string; agentInfo: AgentInfo}): React.ReactElement {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const mentionMatch = matchAgentMention(mentionText, agentInfo)

  if (mentionMatch.isCurrentAgent) {
    if (mentionMatch.type === 'copilot') {
      return (
        <a
          href="https://gh.io/copilot-coding-agent-docs"
          {...getHovercardAttributesForType('copilot', {url: '/copilot/hovercard?bot=copilot-swe-agent'})}
        >
          {mentionText}
        </a>
      )
    }

    if (mentionMatch.type === 'agent') {
      return (
        <a
          href={agentInfo.doc_url}
          {...getHovercardAttributesForType('copilot', {url: `/copilot/hovercard?bot=${agentInfo.slug}`})}
          aria-label={`Information about the ${agentInfo.display_name} agent`}
        >
          {mentionText}
        </a>
      )
    }
  }

  // Not a matching agent mention, render as plain markdown
  return <ExpandableMarkdown markdown={mentionText} />
}
